import { useState, useEffect } from 'react';
import api from '../../../api/axios';
import { MenuGroup, HeaderMenuConfig } from './types';

const NUM_SLOTS = 3;
const FALLBACK_DEFAULTS = ['Home', 'Tracing & Tracking'];

const useHeaderMenu = () => {
    const [menuGroups, setMenuGroups] = useState<MenuGroup[]>([]);
    const [slots, setSlots] = useState<string[]>(['', '', '']);
    const [availableDefaults, setAvailableDefaults] = useState<string[]>(FALLBACK_DEFAULTS);
    const [enabledDefaults, setEnabledDefaults] = useState<string[]>(FALLBACK_DEFAULTS);
    const [isConfigured, setIsConfigured] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [alert, setAlert] = useState<{ open: boolean; type: 'success' | 'error'; message: string }>({
        open: false, type: 'success', message: ''
    });

    // Load menu groups and the current user's own config
    useEffect(() => {
        const load = async () => {
            try {
                setIsLoading(true);
                const [groupsRes, configRes] = await Promise.all([
                    api.get('/menu/groups'),
                    api.get('/header-menu/config'),
                ]);

                setMenuGroups(groupsRes.data || []);

                const config: HeaderMenuConfig = configRes.data;
                setAvailableDefaults(config.available_default_menu || FALLBACK_DEFAULTS);
                setEnabledDefaults(config.default_menu || FALLBACK_DEFAULTS);
                setIsConfigured(config.is_configured);

                const filled = [...(config.additional_menus || [])];
                while (filled.length < NUM_SLOTS) filled.push('');
                setSlots(filled.slice(0, NUM_SLOTS));
            } catch (error: any) {
                console.error('Error loading header menu config:', error);
                setAlert({ open: true, type: 'error', message: 'Failed to load configuration' });
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, []);

    const toggleDefault = (name: string) => {
        setEnabledDefaults(prev =>
            prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
        );
    };

    const setSlot = (index: number, value: string) => {
        setSlots(prev => {
            const next = [...prev];
            next[index] = value;
            return next;
        });
    };

    // Options for a given slot = all groups minus those chosen in other slots (anti-duplicate)
    const optionsForSlot = (index: number): MenuGroup[] => {
        const chosenElsewhere = slots.filter((_, i) => i !== index).filter(Boolean);
        return menuGroups.filter(g => !chosenElsewhere.includes(g.menu_name));
    };

    const save = async () => {
        try {
            setIsSaving(true);
            const additional_menus = slots.filter(Boolean);
            // preserve the available order of default menus
            const default_menu = availableDefaults.filter(d => enabledDefaults.includes(d));
            await api.post('/header-menu/config', { default_menu, additional_menus });
            setIsConfigured(true);
            setAlert({ open: true, type: 'success', message: 'Header menu configuration saved successfully' });
        } catch (error: any) {
            const message = error.response?.data?.message || error.response?.data?.error || 'Failed to save configuration';
            setAlert({ open: true, type: 'error', message });
        } finally {
            setIsSaving(false);
        }
    };

    return {
        data: {
            menuGroups,
            slots,
            availableDefaults,
            enabledDefaults,
            isConfigured,
            isLoading,
            isSaving,
            alert,
        },
        methods: {
            toggleDefault,
            setSlot,
            optionsForSlot,
            save,
            setAlert,
        }
    };
};

export { useHeaderMenu };
